import type { SelfOrderMenu } from "@ramoo/contracts"
import { type KartuProdukData, labelHarga } from "@ramoo/self-order-ui"

/*
 * Logika katalog tamu yang bisa diuji tanpa DOM: penyaringan pencarian, apakah produk perlu detail,
 * validasi pilihan modifier/varian, harga berjalan, dan kunci keranjang. Harga FINAL tetap dihitung
 * server — `hargaSatuan` hanya untuk tampilan berjalan di sheet.
 */

export type Produk = SelfOrderMenu["products"][number]
export type GrupModifier = SelfOrderMenu["modifierGroups"][number]

export interface PilihanItem {
  variantId: string | null
  modifierIds: string[]
  jumlah: number
  notes: string | null
}

/** Produk butuh sheet detail (punya varian atau grup modifier) atau bisa quick-add langsung? */
export const punyaOpsi = (p: Produk): boolean =>
  p.variants.length > 0 || p.modifierGroupIds.length > 0

/** Data kartu presentasional dari satu produk menu. */
export const kartuDari = (p: Produk): KartuProdukData => ({
  id: p.id,
  name: p.name,
  imageUrl: p.imageUrl,
  priceLabel: labelHarga(
    p.price,
    p.variants.map((v) => v.price),
  ),
  hasOptions: punyaOpsi(p),
})

/** Produk bertanda Rekomendasi pemilik, urutan menu dipertahankan; kosong = seksinya tidak tampil. */
export const produkRekomendasi = (menu: Pick<SelfOrderMenu, "products">): Produk[] =>
  menu.products.filter((p) => p.isRecommended)

/** Cocok dengan pencarian nama/deskripsi (case-insensitive). Query kosong = semua cocok. */
export const cocokCari = (p: Produk, query: string): boolean => {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return p.name.toLowerCase().includes(q) || (p.description?.toLowerCase().includes(q) ?? false)
}

/** Grup modifier yang dipakai produk ini (urut sesuai `modifierGroupIds`), lewati id yang tak dikenal. */
export const grupProduk = (p: Produk, semua: GrupModifier[]): GrupModifier[] =>
  p.modifierGroupIds
    .map((id) => semua.find((g) => g.id === id))
    .filter((g): g is GrupModifier => !!g)

/** Berapa modifier dari grup ini yang terpilih. */
export const jumlahTerpilih = (g: GrupModifier, modifierIds: readonly string[]): number =>
  modifierIds.filter((id) => g.modifiers.some((m) => m.id === id)).length

/** Grup sah bila jumlah terpilih dalam [minSelect, maxSelect]; `maxSelect 0` = tak terbatas. */
export const grupValid = (g: GrupModifier, modifierIds: readonly string[]): boolean => {
  const n = jumlahTerpilih(g, modifierIds)
  return n >= g.minSelect && (g.maxSelect === 0 || n <= g.maxSelect)
}

/** Pilihan lengkap: varian dipilih bila produk punya varian, dan semua grup wajib terpenuhi. */
export const pilihanLengkap = (p: Produk, grup: GrupModifier[], pilihan: PilihanItem): boolean => {
  if (p.variants.length > 0 && !pilihan.variantId) return false
  return grup.every((g) => grupValid(g, pilihan.modifierIds))
}

/** Harga satuan berjalan = harga varian (atau base) + jumlah delta modifier terpilih. */
export const hargaSatuan = (p: Produk, grup: GrupModifier[], pilihan: PilihanItem): number => {
  const base = pilihan.variantId
    ? (p.variants.find((v) => v.id === pilihan.variantId)?.price ?? p.price)
    : p.price
  const delta = grup
    .flatMap((g) => g.modifiers)
    .filter((m) => pilihan.modifierIds.includes(m.id))
    .reduce((a, m) => a + m.priceDelta, 0)
  return base + delta
}

/** Batas jumlah per pesanan; `maxPerOrder null` = tak terbatas. */
export const batasJumlah = (p: Produk): number => p.maxPerOrder ?? Number.POSITIVE_INFINITY

/**
 * Kunci keranjang unik per konfigurasi (produk + varian + set modifier + catatan) supaya dua
 * konfigurasi berbeda dari produk yang sama jadi baris terpisah, dan yang identik menyatu.
 */
export const kunciItem = (productId: string, pilihan: PilihanItem): string =>
  `${productId}:${pilihan.variantId ?? ""}:${[...pilihan.modifierIds].sort().join(",")}:${pilihan.notes ?? ""}`
